// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import ColorPickerField from "src/components/shared/inputs/ColorPickerField/ColorPickerField";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import ExamCardModal from "src/components/shared/modals/ExamCardModal/ExamCardModal";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import { SelectChangeEvent } from "@mui/material/Select";
import ExamCardButton from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardButton/ExamCardButton";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";

// Hooks
import { useProgressiveLensTypes } from "src/services/clinics/hooks/clinic-lenses/progressive-lenses/useProgressiveLensTypes";
import { useProgressiveIndexTypes } from "src/services/clinics/hooks/clinic-lenses/progressive-lenses/useProgressiveIndexTypes";

// Types
import {
  AddEditProgressiveLensType,
  ProgressiveLensType,
} from "src/services/clinics/types";

// Constants
import { tintOptions } from "src/constants/enums";

// Utils
import { PROGRESSIVE_ENUM_FIELDS } from "src/utils/exams/lensSimulationHelper";

const DEFAULT_LENS_VALUES: Record<
  string,
  { enumValues: Record<string, string>; indexType: string }
> = {
  EntryLevel: {
    enumValues: {
      corridorLengthType: "Medium",
      corridorWidthType: "Narrow",
      depthDistortType: "Moderate",
    },
    indexType: "Standard",
  },
  Standard: {
    enumValues: {
      corridorLengthType: "Medium",
      corridorWidthType: "Medium",
      depthDistortType: "Moderate",
    },
    indexType: "Thin",
  },
  Premium: {
    enumValues: {
      corridorLengthType: "Medium",
      corridorWidthType: "Wide",
      depthDistortType: "Mild",
    },
    indexType: "Thin",
  },
  OfficeIndoor: {
    enumValues: {
      corridorLengthType: "Long",
      corridorWidthType: "Wide",
      depthDistortType: "Mild",
    },
    indexType: "Standard",
  },
  SportWrap: {
    enumValues: {
      corridorLengthType: "Short",
      corridorWidthType: "Medium",
      depthDistortType: "Mild",
    },
    indexType: "Polycarbonate",
  },
};

type AddEditProgressiveModalType = {
  open: boolean;
  title: string;
  onClose: () => void;
  onConfirm: (lensData: AddEditProgressiveLensType) => void;
  isUpdating: boolean;
  lensToEdit?: ProgressiveLensType | undefined;
};

const AddEditProgressiveModal = ({
  open,
  title,
  onClose,
  onConfirm,
  isUpdating,
  lensToEdit,
}: AddEditProgressiveModalType) => {
  const { t } = useTranslation();

  const { data: lensTypeData, isLoading: isLoadingLensTypes } =
    useProgressiveLensTypes();
  const { data: indexTypeData, isLoading: isLoadingIndexTypes } =
    useProgressiveIndexTypes();

  const formattedLensOptions = useMemo(
    () =>
      (lensTypeData ?? [])?.map((data) => ({
        displayName: t(data),
        value: data,
      })),
    [t, lensTypeData]
  );

  const formattedIndexOptions = useMemo(
    () =>
      (indexTypeData ?? [])?.map((data) => ({
        displayName: t(data),
        value: data,
      })),
    [t, indexTypeData]
  );

  const formattedTintOptions = useMemo(() => {
    const tintArray = Object.values(tintOptions);
    return tintArray.map((tint) => ({
      displayName: t(tint.label),
      value: tint.value,
    }));
  }, [t]);

  const tintDensityOptions = useMemo(
    () => [
      { displayName: t("Low"), value: "Low" },
      { displayName: t("Medium"), value: "Medium" },
      { displayName: t("High"), value: "High" },
    ],
    [t]
  );

  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [lensType, setLensType] = useState({ displayName: "", value: "" });
  const [indexType, setIndexType] = useState({ displayName: "", value: "" });
  const [enumValues, setEnumValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      PROGRESSIVE_ENUM_FIELDS.map((field) => [field.parameterName, ""])
    )
  );
  const [tintColor, setTintColor] = useState({
    displayName: "",
    value: "",
  });
  const [tintDensity, setTintDensity] = useState({
    displayName: "",
    value: "",
  });
  const [blueLightFilter, setBlueLightFilter] = useState(false);
  const [tintPropertiesEnabled, setTintPropertiesEnabled] = useState(false);

  useEffect(() => {
    if (lensToEdit && open) {
      setName(lensToEdit.name);
      setPrice(lensToEdit.price ?? "");
      setLensType({
        displayName: t(lensToEdit.lensType),
        value: lensToEdit.lensType,
      });
      setIndexType({
        displayName: t(lensToEdit.indexType),
        value: lensToEdit.indexType,
      });
      setEnumValues(
        Object.fromEntries(
          PROGRESSIVE_ENUM_FIELDS.map((field) => {
            const currentValue = lensToEdit[field.parameterName];
            const isValid = (field.options as readonly string[]).includes(
              currentValue
            );
            return [field.parameterName, isValid ? currentValue : ""];
          })
        )
      );

      // if the tint name doesn't exist, use "Custom"
      const tintName =
        lensToEdit?.tintColor && lensToEdit?.tintColor !== "Undefined"
          ? formattedTintOptions.find(
              (tint) => lensToEdit?.tintColor === tint.value
            )?.displayName ?? "Custom"
          : undefined;

      setTintColor(
        tintName &&
          lensToEdit?.tintColor &&
          lensToEdit.tintColor !== "Undefined"
          ? {
              displayName: t(tintName),
              value: lensToEdit.tintColor,
            }
          : { displayName: "", value: "" }
      );
      setTintDensity(
        lensToEdit?.tintDensity && lensToEdit.tintDensity !== "Undefined"
          ? {
              displayName: t(lensToEdit.tintDensity),
              value: lensToEdit.tintDensity,
            }
          : { displayName: "", value: "" }
      );
      setBlueLightFilter(lensToEdit.blueLightFilter ?? false);
      setTintPropertiesEnabled(
        Boolean(
          (lensToEdit?.tintColor && lensToEdit?.tintColor !== "Undefined") ||
            (lensToEdit?.tintDensity && lensToEdit?.tintDensity !== "Undefined")
        )
      );
    } else if (!lensToEdit && open) {
      setEnumValues(
        Object.fromEntries(
          PROGRESSIVE_ENUM_FIELDS.map((field) => [
            field.parameterName,
            field.options[0],
          ])
        )
      );
      setTintColor({ displayName: "", value: "" });
      setTintDensity({ displayName: "", value: "" });
      setBlueLightFilter(false);
      setTintPropertiesEnabled(false);
    }
  }, [lensToEdit, open, formattedTintOptions, t]);

  const handleSetLensOption = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = formattedLensOptions?.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        setLensType({ ...value });

        // set default values when first selecting a lens type
        const defaults = DEFAULT_LENS_VALUES[value.value];
        if (defaults) {
          setEnumValues(defaults.enumValues);

          const indexOption = formattedIndexOptions?.find(
            (option) => option.value === defaults.indexType
          );
          setIndexType(
            indexOption ?? {
              displayName: t(defaults.indexType),
              value: defaults.indexType,
            }
          );
        }
      }
    },
    [formattedLensOptions, formattedIndexOptions, t]
  );

  const handleSetIndexType = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = formattedIndexOptions?.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        setIndexType({ ...value });
      }
    },
    [formattedIndexOptions]
  );

  const handleSetTintColor = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = formattedTintOptions?.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        // tintOptions.CUSTOM.value is "" -- default the custom color
        // picker to black instead of leaving it empty the moment Custom
        // is selected.
        const isCustom = value.displayName === t("Custom");
        setTintColor({
          ...value,
          value: isCustom ? "#000000" : value.value,
        });
      }
    },
    [formattedTintOptions, t]
  );

  const handleSetTintDensity = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = tintDensityOptions?.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        setTintDensity({ ...value });
      }
    },
    [tintDensityOptions]
  );

  const handleSetCustomTintColorValue = useCallback((hex: string) => {
    setTintColor((prev) => ({ ...prev, value: hex }));
  }, []);

  const isCustomTintSelected = tintColor.displayName === t("Custom");

  const shouldDisable = useMemo(
    () =>
      !name ||
      !lensType.value ||
      !indexType.value ||
      (tintPropertiesEnabled && (!tintColor.value || !tintDensity.value)),
    [
      name,
      lensType.value,
      indexType.value,
      tintPropertiesEnabled,
      tintColor.value,
      tintDensity.value,
    ]
  );

  const resetTintValues = useCallback(() => {
    setTintColor({ displayName: "", value: "" });
    setTintDensity({ displayName: "", value: "" });
  }, []);

  const handleToggleTintProperties = useCallback(() => {
    setTintPropertiesEnabled((prev) => {
      const next = !prev;
      if (!next) resetTintValues();
      return next;
    });
  }, [resetTintValues]);

  const resetValues = useCallback(() => {
    setName("");
    setPrice("");
    setLensType({ displayName: "", value: "" });
    setIndexType({ displayName: "", value: "" });
    setEnumValues(
      Object.fromEntries(
        PROGRESSIVE_ENUM_FIELDS.map((field) => [field.parameterName, ""])
      )
    );
    setBlueLightFilter(false);
    resetTintValues();
    setTintPropertiesEnabled(false);
  }, [resetTintValues]);

  const handleClose = useCallback(() => {
    resetValues();
    onClose();
  }, [resetValues, onClose]);

  const handleConfirm = useCallback(() => {
    const lensData = {
      name,
      price,
      lensType: lensType.value,
      indexType: indexType.value,
      ...Object.fromEntries(
        PROGRESSIVE_ENUM_FIELDS.map((field) => [
          field.parameterName,
          enumValues[field.parameterName],
        ])
      ),
      tintColor: tintColor.value,
      tintDensity: tintDensity.value,
      blueLightFilter,
    } as AddEditProgressiveLensType;
    resetValues();
    onConfirm(lensData);
  }, [
    name,
    price,
    lensType.value,
    indexType.value,
    enumValues,
    tintColor.value,
    tintDensity.value,
    blueLightFilter,
    resetValues,
    onConfirm,
  ]);

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  const tooltipStyle = {
    color: "#FFF",
    fontSize: "12px",
    width: "16px",
  };

  return (
    <ExamCardModal
      backgroundColor="#202338"
      isOpen={open}
      title={title}
      onClose={handleClose}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            data-testid="button-save"
            disabled={shouldDisable}
            sx={{
              ...ModalCustomConfirm(),
              "&.Mui-disabled": {
                color: "#FFF",
                backgroundColor: "#435B6D7A",
              },
            }}
            onClick={handleConfirm}
          >
            {t("Save")}
          </Button>
        </Grid>
      }
    >
      <Grid
        display="flex"
        flexDirection="column"
        p="8px"
        gap="8px"
        sx={{ backgroundColor: "#202338" }}
      >
        {/* Row 1: Name + Lens Type */}
        <Box display="flex" gap="8px">
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography color="#FFF" fontSize="12px">
              {t("Name")}*
            </Typography>
            <TextInputField
              id="progressive-name-input"
              required
              disabled={isLoadingLensTypes || isUpdating}
              autoFocus={!lensToEdit}
              placeholder={t("Enter a value")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <TitleWithTooltip
              title={`${t("Lens Type")}*`}
              tooltip={
                <div>{t("Sets the lens design and default values.")}</div>
              }
              titleStyle={textStyle}
              tooltipStyle={tooltipStyle}
            />
            <SelectDropdown
              id="progressive-lens-type-dropdown"
              disabled={isLoadingLensTypes || isUpdating}
              dropdownOptions={formattedLensOptions}
              value={lensType.displayName}
              handleChange={handleSetLensOption}
            />
          </Box>
        </Box>

        {/* Row 2: Index Type + Price */}
        <Box display="flex" gap="8px">
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography color="#FFF" fontSize="12px">
              {t("Price")}
            </Typography>
            <TextInputField
              id="progressive-price-input"
              required
              disabled={isLoadingLensTypes || isUpdating}
              placeholder={t("Enter a value")}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap="4px"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <TitleWithTooltip
              title={`${t("Index Type")}*`}
              tooltip={
                <div>{t("Controls the amount of chromatic aberration.")}</div>
              }
              titleStyle={textStyle}
              tooltipStyle={tooltipStyle}
            />
            <SelectDropdown
              id="progressive-index-type-dropdown"
              disabled={isLoadingLensTypes || isLoadingIndexTypes || isUpdating}
              dropdownOptions={formattedIndexOptions}
              value={indexType.displayName}
              handleChange={handleSetIndexType}
            />
          </Box>
        </Box>

        {/* Rows 3-5: Corridor Length, Corridor Width, Peripheral Distortion */}
        {PROGRESSIVE_ENUM_FIELDS.map(
          ({ parameterName, title, tooltip, options }) => (
            <Box display="flex" gap="8px" key={parameterName}>
              <Box
                display="flex"
                flexDirection="column"
                gap="4px"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <TitleWithTooltip
                  title={`${t(title)}*`}
                  tooltip={<div>{t(tooltip)}</div>}
                  titleStyle={textStyle}
                  tooltipStyle={tooltipStyle}
                />
                <Box display="flex" gap="4px">
                  {options.map((option) => (
                    <ExamCardButton
                      key={`${parameterName}-${option}`}
                      label={t(option)}
                      isSelected={enumValues[parameterName] === option}
                      isDisabled={isLoadingLensTypes || isUpdating}
                      updateValue={() =>
                        setEnumValues((prev) => ({
                          ...prev,
                          [parameterName]: option,
                        }))
                      }
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )
        )}

        {/* Row 6: Enable Tint Properties + Blue Light Filter */}
        <Box display="flex" alignItems="center" gap="24px">
          <Box display="flex" alignItems="center" gap="8px">
            <CustomCheckbox
              id="blue-light-filter-checkbox"
              checked={blueLightFilter}
              onClick={() => setBlueLightFilter((prev) => !prev)}
              disabled={isLoadingLensTypes || isUpdating}
              checkedFill="#E2772E"
              checkedStroke="#FFF"
              uncheckedFill="#FFF"
              uncheckedStroke="#5B6D7A"
            />
            <TitleWithTooltip
              title={`${t("Blue Light Filter")}`}
              tooltip={
                <div>
                  {t(
                    "Reduces some blue light from screens; may add a slight warm tint."
                  )}
                </div>
              }
              titleStyle={textStyle}
              tooltipStyle={tooltipStyle}
            />
          </Box>
          <Box display="flex" alignItems="center" gap="8px">
            <CustomCheckbox
              id="lens-tint-checkbox"
              checked={tintPropertiesEnabled}
              onClick={handleToggleTintProperties}
              disabled={isLoadingLensTypes || isUpdating}
              checkedFill="#E2772E"
              checkedStroke="#FFF"
              uncheckedFill="#FFF"
              uncheckedStroke="#5B6D7A"
            />
            <Typography color="#FFF" fontSize="12px">
              {t("Lens Tint")}
            </Typography>
          </Box>
        </Box>

        {tintPropertiesEnabled && (
          <>
            {/* Row 7: Tint Color + Custom Tint Color */}
            <Box display="flex" gap="8px">
              <Box
                display="flex"
                flexDirection="column"
                gap="4px"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <TitleWithTooltip
                  title={`${t("Tint Color")}*`}
                  tooltip={
                    <div>
                      {t(
                        "Color and darkness of the lens tint. Darker tints are for outdoor use; not recommended for night driving."
                      )}
                    </div>
                  }
                  titleStyle={textStyle}
                  tooltipStyle={tooltipStyle}
                />
                <SelectDropdown
                  id="progressive-tint-color-dropdown"
                  disabled={isLoadingLensTypes || isUpdating}
                  dropdownOptions={formattedTintOptions}
                  value={tintColor.displayName}
                  handleChange={handleSetTintColor}
                />
              </Box>
              {isCustomTintSelected && (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="4px"
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Typography color="#FFF" fontSize="12px">
                    {t("Custom Tint Color")}*
                  </Typography>
                  <ColorPickerField
                    value={tintColor.value}
                    onChange={handleSetCustomTintColorValue}
                    disabled={isLoadingLensTypes || isUpdating}
                  />
                </Box>
              )}
            </Box>

            {/* Row 8: Tint Density */}
            <Box display="flex" gap="8px">
              <Box
                display="flex"
                flexDirection="column"
                gap="4px"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <TitleWithTooltip
                  title={`${t("Tint Density")}*`}
                  tooltip={
                    <div>
                      {t(
                        "Color and darkness of the lens tint. Darker tints are for outdoor use; not recommended for night driving."
                      )}
                    </div>
                  }
                  titleStyle={textStyle}
                  tooltipStyle={tooltipStyle}
                />
                <SelectDropdown
                  id="progressive-tint-density-dropdown"
                  disabled={isLoadingLensTypes || isUpdating}
                  dropdownOptions={tintDensityOptions}
                  value={tintDensity.displayName}
                  handleChange={handleSetTintDensity}
                />
              </Box>
            </Box>
          </>
        )}
      </Grid>
    </ExamCardModal>
  );
};

export default AddEditProgressiveModal;
